import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PencilSquareIcon, CodeBracketIcon } from '@heroicons/react/24/solid';

// --- Data for the page (remains the same) ---
const manualTutorials = [
    {
        title: 'How to Write Effective Test Cases',
        description: 'Learn the fundamentals of creating clear, concise, and comprehensive test cases that anyone can understand and execute.',
        level: 'Beginner',
        link: 'https://www.coursera.org/articles/how-to-write-test-cases',
    },
    {
        title: 'Understanding Test Scenarios vs. Test Cases',
        description: 'Discover the key differences between test scenarios and test cases, and learn when and how to use each effectively.',
        level: 'Beginner',
        link: 'https://www.geeksforgeeks.org/difference-between-test-case-and-scenarios/',
    },
    {
        title: 'Mastering the Art of Bug Reporting',
        description: 'A great bug report is key to a fast fix. Learn how to write detailed reports with steps to reproduce, screenshots, and logs.',
        level: 'Beginner',
        link: 'https://blog.bugv.io/the-art-of-bug-reporting-mastering-the-dos-and-donts-for-effective-collaboration/',
    },
    {
        title: 'An Introduction to Exploratory Testing',
        description: 'Move beyond scripted tests. Understand the principles of exploratory testing to find critical bugs that scripts might miss.',
        level: 'Intermediate',
        link: 'https://www.atlassian.com/continuous-delivery/software-testing/exploratory-testing',
    },
];

const automationTutorials = [
    {
        title: 'Setting Up Your First Selenium Project',
        description: 'A step-by-step guide to installing and configuring Selenium WebDriver with Java/Python to start your automation journey.',
        level: 'Beginner',
        link: 'https://www.qatouch.com/blog/selenium-java-webdriver/',
    },
    {
        title: 'Writing Your First Automated Script with Cypress',
        description: 'Dive into modern web automation with Cypress. We’ll guide you through writing a simple but powerful login test.',
        level: 'Beginner',
        link: 'https://www.lambdatest.com/blog/cypress-test/',
    },
    {
        title: 'API Testing Basics with Postman',
        description: 'Learn how to send requests, inspect responses, and write basic API tests using the industry-standard tool, Postman.',
        level: 'Intermediate',
        link: 'https://www.postman.com/api-platform/api-testing/',
    },
];

// --- Animation Variants ---
const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
        },
    },
};

const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const cardHover = {
    y: -10,
    boxShadow: "0px 20px 30px rgba(0,0,0,0.1)",
    transition: { type: "spring", stiffness: 300 }
};

const TutorialsPage = () => {
    return (
        <div className="space-y-24">
            {/* Page Header */}
            <motion.div
                className="text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                    QA & Testing Tutorials
                </h1>
                <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-600">
                    From the basics to advanced techniques, our tutorials are designed to build your skills step by step.
                </p>
            </motion.div>

            {/* Manual Testing Section */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.3 }}
            >
                <div className="flex items-center mb-8">
                    <PencilSquareIcon className="h-8 w-8 text-indigo-600 mr-3"/>
                    <h2 className="text-3xl font-bold text-gray-900">Manual Testing Fundamentals</h2>
                </div>
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    variants={staggerContainer}
                >
                    {manualTutorials.map((tutorial, index) => (
                        <motion.div
                            key={index}
                            variants={cardVariants}
                            whileHover={cardHover}
                        >
                            <Link to={tutorial.link} className="block h-full">
                                <div className="flex flex-col justify-between h-full bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                                    <div className="p-6">
                                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                                            tutorial.level === 'Beginner' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {tutorial.level}
                                        </span>
                                        <h3 className="mt-4 text-xl font-semibold text-gray-900">{tutorial.title}</h3>
                                        <p className="mt-2 text-gray-600">{tutorial.description}</p>
                                    </div>
                                    <div className="p-6 bg-gray-50 border-t">
                                        <span className="font-semibold text-indigo-600">
                                            Start Tutorial &rarr;
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>

            {/* Automation Testing Section */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.3 }}
            >
                <div className="flex items-center mb-8">
                    <CodeBracketIcon className="h-8 w-8 text-indigo-600 mr-3"/>
                    <h2 className="text-3xl font-bold text-gray-900">Automation Testing Basics</h2>
                </div>
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    variants={staggerContainer}
                >
                    {automationTutorials.map((tutorial, index) => (
                        <motion.div
                            key={index}
                            variants={cardVariants}
                            whileHover={cardHover}
                        >
                            <Link to={tutorial.link} className="block h-full">
                                <div className="flex flex-col justify-between h-full bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                                    <div className="p-6">
                                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                                            tutorial.level === 'Beginner' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {tutorial.level}
                                        </span>
                                        <h3 className="mt-4 text-xl font-semibold text-gray-900">{tutorial.title}</h3>
                                        <p className="mt-2 text-gray-600">{tutorial.description}</p>
                                    </div>
                                    <div className="p-6 bg-gray-50 border-t">
                                        <span className="font-semibold text-indigo-600">
                                           Start Tutorial &rarr;
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>
        </div>
    );
};

export default TutorialsPage;